import { MANUAL_FOLLOW_UP_STEP_ID } from "@/enum/schedule.enum";

import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import { ListPersonSchedule } from "@/pages/Private/pages/List/schema/list";

/**
 * Resolves the `Schedule` record backing a scheduled conversation bubble, by matching
 * `schedule.conversationId === conversation.id` in the listPerson's `schedules` array.
 *
 * Returns `null` when no matching schedule is found.
 */
export function resolveSchedule(
	conversation: Pick<Conversation, "id">,
	schedules?: ListPersonSchedule[]
): ListPersonSchedule | null {
	if (!schedules?.length) {
		return null;
	}

	return schedules.find((s) => s.conversationId === conversation.id) ?? null;
}

/**
 * A scheduled message that has not gone out and is not on its way: not sent (`processedAt`), not
 * cancelled (`canceledAt`), not failed (`failedAt`), not handed to the sender (`publishedAt`).
 *
 * This is the one base every control predicate below derives from, and it deliberately does
 * **not** read `processingState`. That column is generated from the same timestamps plus
 * `scheduledFor`, so a follow-up parked behind a LinkedIn invitation — no send time until the
 * invite is accepted — is `pending`, not `scheduled`, and a paused one is still `pending`. A
 * predicate written as `processingState === SCHEDULED` hid every control for every parked row
 * while the API accepted the request (PREC-4220: 77 messages on production with nothing to click).
 */
export function isSchedulePending(schedule: ListPersonSchedule | null): boolean {
	return (
		!!schedule &&
		!schedule.canceledAt &&
		!schedule.processedAt &&
		!schedule.failedAt &&
		!schedule.publishedAt
	);
}

/** A manual message the user wrote in the Outbox composer (`n99`), as opposed to a campaign step. */
function isManualFollowUp(schedule: ListPersonSchedule | null): boolean {
	return schedule?.stepId === MANUAL_FOLLOW_UP_STEP_ID;
}

/**
 * A scheduled message can be cancelled while it is pending — parked behind an invite or not,
 * paused or not. The API also accepts a failed or in-flight row; those have their own affordances
 * (the failure line, the send itself) and are left out here on purpose.
 */
export function isScheduleCancellable(schedule: ListPersonSchedule | null): boolean {
	return isSchedulePending(schedule);
}

/**
 * A manual follow-up still parked behind a LinkedIn invitation (PREC-4220): it waits for the
 * acceptance, which then schedules it `scheduledInDays` days later. The bubble says so instead of
 * showing a time it does not have.
 */
export function isScheduleWaitingForInvite(schedule: ListPersonSchedule | null): boolean {
	return isSchedulePending(schedule) && !!schedule?.gatedByScheduleId;
}

/**
 * A scheduled message the user may pause on their own (PREC-2861): a manual follow-up that is
 * still pending — mirrors the API's `decideManualFollowUpPause`. A campaign step is held through
 * the lead-level "Pause outreach" instead. A follow-up parked behind an invite can be paused too
 * (PREC-4220): on acceptance it receives its send time but stays held until resumed.
 */
export function isSchedulePausable(schedule: ListPersonSchedule | null): boolean {
	return isSchedulePending(schedule) && isManualFollowUp(schedule) && !!schedule?.active;
}

/**
 * A manual follow-up the user paused: still pending, but its `active` flag is off. Cancelling also
 * clears the flag, but a cancelled row is not pending, so it is not a paused one.
 */
export function isSchedulePaused(schedule: ListPersonSchedule | null): boolean {
	return isSchedulePending(schedule) && isManualFollowUp(schedule) && !schedule?.active;
}

/**
 * A manual message whose body, subject and timing the user may still change (PREC-4220) —
 * mirrors the API's `decideManualFollowUpEdit`: pending, manual, paused or not. Campaign steps are
 * edited in the campaign.
 */
export function isScheduleEditable(schedule: ListPersonSchedule | null): boolean {
	return isSchedulePending(schedule) && isManualFollowUp(schedule);
}

/**
 * A manual message the user may send right away (PREC-3103) — mirrors the API's
 * `decideManualFollowUpSendNow`: pending, manual, not parked behind a LinkedIn invitation (it has
 * no chat to go into before the acceptance), and with something to bring forward — a send time,
 * or a pause to release. An active row with no send time is already on its way.
 */
export function isScheduleSendableNow(schedule: ListPersonSchedule | null): boolean {
	return (
		isSchedulePending(schedule) &&
		isManualFollowUp(schedule) &&
		!schedule?.gatedByScheduleId &&
		(!!schedule?.scheduledFor || !schedule?.active)
	);
}
